import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SeoService } from '@services/seo';
import { UrlService } from '@services/url';
import { defaultParams } from '@shared/components/pagination/config';
import { GetLabelPipe, GetPagePipe } from '@shared/components/pagination/pipe';
import { IconsDirective } from '@shared/directives';
import { Subject, takeUntil } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Pages } from './model';

@Component({
  selector: 'app-pagination',
  imports: [NgClass, RouterLink, GetPagePipe, GetLabelPipe, IconsDirective],
  templateUrl: './pagination.component.html',
  styleUrl: './pagination.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaginationComponent implements OnInit, OnDestroy {
  readonly start = input<number>(defaultParams.start);
  readonly limit = input<number>(defaultParams.limit);
  readonly totalCount = input.required<number>();
  readonly isMobile = input(defaultParams.isMobile);
  readonly changePage = output<number>();
  nbPage = signal<number>(null!);
  middlePages = signal<Pages[]>(null!);
  firstPages = signal<Pages[]>(null!);
  endPages = signal<Pages[]>(null!);
  currentPage = signal<number>(null!);
  isPaginationDisplayed = signal(true);
  readonly #urlService = inject(UrlService);
  readonly #seoService = inject(SeoService);
  private destroy$: Subject<boolean> = new Subject<boolean>();

  ngOnInit(): void {
    const totalCount = this.totalCount();
    if (totalCount === undefined) {
      throw new Error(`Input value totalCount is not defined`);
    }
    this.currentPage.set(this.start());
    this.nbPage.set(Math.ceil(totalCount / this.limit()));
    if (this.nbPage() === 0) {
      this.nbPage.set(1);
    }
    this.isPaginationDisplayed.set(this.nbPage() !== 1);
    this.checkPaginationLimit();
    this.#urlService
      .getParams()
      .pipe(
        takeUntil(this.destroy$),
        tap((params) => {
          const pageNumber = Number(params['page']);
          this.currentPage.set(isNaN(pageNumber) ? 1 : pageNumber);
          this.checkPaginationLimit();
          this.updatePagination();
          this.updateTitlePage();
        })
      )
      .subscribe();
  }

  updatePagination(): void {
    const c = this.currentPage();
    const p = this.nbPage();
    if (p > 2) {
      const middlePages: Pages[] = [
        { type: 'page', label: c - 1 },
        {
          type: 'page',
          label: c,
        },
        { type: 'page', label: c + 1 },
      ];
      this.middlePages.set(
        middlePages.filter((item) => typeof item.label === 'number' && item.label > 1 && item.label < p)
      );
      if (this.isMiddlePageFound(2)) {
        this.firstPages.set([
          { type: 'previous', label: 'Précédent', isDisabled: this.currentPage() === 1 },
          { type: 'page', label: 1 },
        ]);
      } else if (c > 1) {
        this.firstPages.set([
          { type: 'previous', label: 'Précédent' },
          { type: 'page', label: 1 },
          {
            type: 'back5',
            label: '...',
          },
        ]);
      }
      if (!this.isMiddlePageFound(p - 1)) {
        this.endPages.set([
          { type: 'skip5', label: '...' },
          { type: 'page', label: p },
          { type: 'next', label: 'Suivant' },
        ]);
      } else {
        this.endPages.set([
          { type: 'page', label: p },
          { type: 'next', label: 'Suivant', isDisabled: this.currentPage() === p },
        ]);
      }
    } else {
      this.firstPages.set([{ type: 'previous', label: 'Précédent', isDisabled: this.currentPage() === 1 }]);
      this.endPages.set([{ type: 'next', label: 'Suivant', isDisabled: this.currentPage() === p }]);
    }
    if (this.currentPage() > 1) this.#urlService.setParams({ page: String(this.currentPage()) });
    this.updateLinkRel();
    this.changePage.emit(this.currentPage());
  }

  ngOnDestroy(): void {
    this.#seoService.removeLinkTag('prev');
    this.#seoService.removeLinkTag('next');
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private setTitlePageNumber(title: string, page: number): void {
    this.#seoService.updateTitle(`${title} - Page ${page}`);
  }

  private updateTitlePageNumber(title: string, page: number): void {
    const regex = / - Page \d+/;
    const newTitle = title.replace(regex, ` - Page ${page}`);
    this.#seoService.updateTitle(newTitle);
  }

  private removeTitlePageNumber(title: string): void {
    const regex = / - Page \d+/;
    const newTitle = title.replace(regex, '');
    this.#seoService.updateTitle(newTitle);
  }

  private isMiddlePageFound(p: number): boolean {
    return !!this.middlePages().find((page) => page.label === p);
  }

  private checkPaginationLimit(): void {
    if (this.currentPage() > this.nbPage()) {
      this.currentPage.set(this.nbPage());
    } else if (this.currentPage() < 1) {
      this.currentPage.set(1);
    }
  }

  private updateLinkRel(): void {
    const c = this.currentPage();
    const p = this.nbPage();
    if (c - 1 > 0) {
      const page = String(c - 1) === '1' ? '' : `?page=${String(c - 1)}`;
      this.#seoService.setLinkTag('prev', `${this.#seoService.canonical()}${page}`);
    } else {
      this.#seoService.removeLinkTag('prev');
    }
    if (c + 1 <= p) {
      this.#seoService.setLinkTag('next', `${this.#seoService.canonical()}?page=${String(c + 1)}`);
    } else {
      this.#seoService.removeLinkTag('next');
    }
  }

  private updateTitlePage(): void {
    const currentPage = this.currentPage();
    const title = this.#seoService.getTitle();
    if (currentPage > 1) {
      const isPageInTitle = this.isPageNumberInTitle(title);
      if (isPageInTitle) {
        this.updateTitlePageNumber(title, currentPage);
      } else {
        this.setTitlePageNumber(title, currentPage);
      }
    } else if (this.isPageNumberInTitle(title)) {
      this.removeTitlePageNumber(title);
    }
  }

  private isPageNumberInTitle(title: string): boolean {
    const regex = / - Page \d+/;
    return regex.test(title);
  }
}
